// The event-driven command path through a location node (ops plan 2026-10-16-command-latency; Andrew, 2026-10-16: a
// Commander command reaches the register and reports back within 5 s at p95). Two held requests replace the two
// 60-second beacon cycles that set today's 65 to 125 s:
//   - the node's agent long-polls the cloud (`POST /node/v1/poll`, signed exactly like a beacon upload): the cloud answers
//     at once when anything waits for the node and otherwise holds the request up to NODE_POLL_HOLD_MS, answering the
//     moment a row is written. The request carries the units' command answers up at once;
//   - a unit's pull may ask its receiver to hold it (`PullRequest.waitMs`) when nothing is new for it, but only after the
//     receiver's answer said it holds pulls (`PullResponse.holdsPulls`): a receiver before it refuses the field.
// The beacon is unchanged (health every 60 s, and its answer still hands everything over), so with either switch off
// the old path stands.
import { z } from 'zod';
import { IsoInstant } from '../primitives/codecs.ts';
import { DeviceAlert, DeviceCommandName, DeviceCommandResult, DeviceLabOrder } from './protocol.ts';

/** How long the cloud holds a node's poll, and a receiver a unit's pull, when nothing waits. */
export const NODE_POLL_HOLD_MS = 25_000;
export const HELD_PULL_MAX_MS = 25_000;

/** A device command the cloud hands to a node for its unit (as a beacon answer's `deviceCommands`). */
export const RelayedDeviceCommand = z.object({
  commandId: z.uuid(), deviceId: z.uuid(), command: DeviceCommandName, issuedAt: z.iso.datetime(), expiresAt: z.iso.datetime(),
  /** `showAlert`'s type and message; alerts come in the answer's own `deviceAlerts` list. */
  alert: DeviceAlert.optional(),
  /** `labCondition`'s order; lab commands come in the answer's own `labCommands` list, to a lab node's agent only. */
  lab: DeviceLabOrder.optional(),
  /**
   * `collectInfo`'s offer from the cloud (support-diagnostics, lane X3): the facts the cloud parses. A hub keeps only
   * those it also parses (`forwardedInfoKeys`); an older agent drops the field and offers nothing.
   */
  infoKeys: z.array(z.string().max(64)).max(64).optional(),
});
export type RelayedDeviceCommand = z.infer<typeof RelayedDeviceCommand>;

/** One declared check as the cloud hands it over, signed with its command key (doc 18). */
export const NodeDiagnosticCommand = z.object({
  requestId: z.uuid(),
  nodeId: z.uuid(),
  check: z.record(z.string(), z.unknown()),
  issuedAt: z.int().positive(),
  expiresAt: z.int().positive(),
  signature: z.string().min(16).max(200),
});
export type NodeDiagnosticCommand = z.infer<typeof NodeDiagnosticCommand>;

/** One lab condition as the cloud hands it to its node, signed with the command key in the lab domain. */
export const NodeLabCondition = z.object({
  conditionId: z.uuid(),
  nodeId: z.uuid(),
  condition: z.record(z.string(), z.unknown()),
  issuedAt: z.int().positive(),
  expiresAt: z.int().positive(),
  signature: z.string().min(16).max(200),
});
export type NodeLabCondition = z.infer<typeof NodeLabCondition>;

/** Every lab condition active for a lab node now (an empty list clears them all). */
export const NodeLabAnswer = z.object({ conditions: z.array(NodeLabCondition).max(16) });
export type NodeLabAnswer = z.infer<typeof NodeLabAnswer>;

/**
 * A node's poll, signed with its identity key over the exact payload (as a beacon upload is). `results` are its units'
 * command answers since its last poll or beacon, sent at once (each by the unit's replica); `commandReads` says what the
 * agent keeps from an answer, as a beacon's does.
 */
export const NodePollRequest = z.strictObject({
  kind: z.literal('poll'),
  nodeId: z.uuid(),
  serial: z.string().max(64),
  sentAt: z.int().positive(),
  commandReads: z.array(z.string().max(32)).max(8),
  results: z.array(DeviceCommandResult.extend({ replica: z.string().max(64) })).max(50),
  /**
   * The lab conditions a lab node has entered now (its agent's set). The cloud answers with `lab` only when its own set
   * differs; absent (an agent before it, or not a lab node) and it never does, and the beacon keeps handing them over.
   */
  labConditionIds: z.array(z.uuid()).max(16).optional(),
});
export type NodePollRequest = z.infer<typeof NodePollRequest>;

/**
 * The cloud's answer: what a beacon answer hands over (device commands, alerts, a lab node's lab commands and lab
 * conditions, declared checks), at once when any waits, else when one is written or at NODE_POLL_HOLD_MS with nothing.
 */
export const NodePollAnswer = z.object({
  serverTime: z.int(),
  /** Whether the cloud held the request (false: something was waiting, or it was answered at once). */
  held: z.boolean(),
  deviceCommands: z.array(RelayedDeviceCommand).max(200),
  deviceAlerts: z.array(RelayedDeviceCommand).max(200).optional(),
  labCommands: z.array(RelayedDeviceCommand).max(200).optional(),
  /** Commander's screen views (`screenView`), only to an agent whose `commandReads` include `screen_view` (lane SV). */
  screenCommands: z.array(RelayedDeviceCommand).max(200).optional(),
  /** Log uploads (`uploadLogs`, support diagnostics), only to an agent whose `commandReads` include `upload_logs`. */
  logCommands: z.array(RelayedDeviceCommand).max(200).optional(),
  lab: NodeLabAnswer.optional(),
  diagnostics: z.array(NodeDiagnosticCommand).max(5).optional(),
  /** When the cloud recorded the request's results (null: it carried none). */
  resultsRecordedAt: IsoInstant.nullable(),
  /**
   * The cloud asks this hub to hold its WebSocket instead of polling (ops plan 2026-10-16-command-latency, the
   * coordinator's design B: the cloud's RADISH_NODE_SOCKET_HUBS names the hubs). An agent before it ignores the field,
   * and an agent whose own RADISH_NODE_SOCKET is off ignores it too. Absent: keep polling.
   */
  socket: z.boolean().optional(),
});
export type NodePollAnswer = z.infer<typeof NodePollAnswer>;
